import React, {useEffect, useRef} from 'react';
import * as AC from 'adaptivecards';
import {Action} from 'adaptivecards/lib/card-elements';

// The data payload we expect from the C# tool
interface ActionPayloadData {
    verb?: 'approve' | 'reject'; // The verb identifies the action taken
    toolCallId?: string; // The ID to correlate this action
    [key: string]: unknown;
}

// The structure of an action in the card's JSON payload
interface AdaptiveCardActionPayload {
    type: string;
    title?: string;
    data?: ActionPayloadData;
    style?: 'positive' | 'destructive' | 'default';
    [key: string]: unknown;
}

// The overall structure of the card payload
interface AdaptiveCardPayloadForViewer {
    type: string;
    version: string;
    body: unknown[];
    actions?: AdaptiveCardActionPayload[];
}

interface AdaptiveCardViewerProps {
    payload: object; // The raw JSON payload for the card
    hostConfig?: Partial<AC.HostConfig>;
    onExecuteAction?: (action: AC.ExecuteAction) => void;
    selectedActionVerb?: 'approve' | 'reject' | undefined;
}

const AdaptiveCardViewer: React.FC<AdaptiveCardViewerProps> = ({
    payload,
    hostConfig,
    onExecuteAction,
    selectedActionVerb
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!containerRef.current || !payload) {
            return;
        }

        const cardPayload = payload as AdaptiveCardPayloadForViewer;
        const payloadActions = cardPayload.actions || [];

        const adaptiveCard = new AC.AdaptiveCard();
        adaptiveCard.hostConfig = new AC.HostConfig(hostConfig || {});

        // Only attach the click handler if an action has NOT been selected yet
        if (!selectedActionVerb && onExecuteAction) {
            adaptiveCard.onExecuteAction = (action: Action) => {
                // We are primarily interested in ExecuteAction
                if (action instanceof AC.ExecuteAction) {
                    console.log('Action executed:', action);
                    // The `action.data` will contain { verb: '...', toolCallId: '...' }
                    onExecuteAction(action);
                } else if (action instanceof AC.OpenUrlAction) {
                    console.log('Opening URL:', action.url);
                    if (action.url) {
                        window.open(action.url, '_blank');
                    }
                }
            };
        }

        try {
            adaptiveCard.parse(payload);
            const renderedCard = adaptiveCard.render();

            if (containerRef.current && renderedCard) {
                // Clear previous content and append the new card
                containerRef.current.innerHTML = '';
                containerRef.current.appendChild(renderedCard);

                // --- Modify Rendered Buttons Based on State ---
                const buttons = containerRef.current.querySelectorAll(
                    'button.ac-pushButton'
                );

                buttons.forEach((button, index) => {
                    if (!(button instanceof HTMLButtonElement)) return;

                    const actionDef = payloadActions[index];
                    // Get the verb from our new data structure
                    const currentActionVerb = actionDef?.data?.verb;

                    // Apply base Bootstrap classes and remove default AC class
                    button.classList.add('btn', 'me-2', 'mb-2');
                    button.classList.remove('ac-pushButton');

                    // If a verb has been selected, the card becomes inactive
                    if (selectedActionVerb) {
                        button.disabled = true;

                        // Style the selected button as primary and others as secondary
                        if (
                            currentActionVerb &&
                            currentActionVerb === selectedActionVerb
                        ) {
                            button.classList.add('btn-primary'); // Solid, selected style
                        } else {
                            button.classList.add('btn-secondary', 'opacity-50'); // Muted, unselected style
                        }
                    } else {
                        // Card is active, style based on the action's intent
                        button.disabled = false;
                        switch (actionDef?.style) {
                            case 'positive':
                                button.classList.add('btn-outline-success');
                                break;
                            case 'destructive':
                                button.classList.add('btn-outline-danger');
                                break;
                            default:
                                button.classList.add('btn-outline-primary');
                                break;
                        }
                    }
                });
            }
        } catch (error) {
            console.error('Error rendering Adaptive Card:', error);
            if (containerRef.current) {
                containerRef.current.innerHTML =
                    '<div class="alert alert-danger">Error rendering card. See console for details.</div>';
            }
        }
    }, [payload, hostConfig, onExecuteAction, selectedActionVerb]);

    return <div ref={containerRef} className="adaptive-card-host" />;
};

export default AdaptiveCardViewer;
